import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_INTERVENTION_PRIORITE_LABELS,
  AGENCE_INTERVENTION_PRIORITE_OPTIONS,
  AGENCE_INTERVENTION_PRIORITE_TONES,
  AGENCE_INTERVENTION_STATUT_LABELS,
  AGENCE_INTERVENTION_STATUT_OPTIONS,
  AGENCE_INTERVENTION_STATUT_TONES,
  type AgenceIntervention,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";
import InterventionForm from "./components/InterventionForm";

type SortKey = "recent" | "ancien" | "cout";

const sortOptions = [
  { value: "recent", label: "Plus récentes" },
  { value: "ancien", label: "Plus anciennes" },
  { value: "cout", label: "Coût décroissant" },
];

export default function AgenceMaintenancePage() {
  const { interventions, statsInterventions, immeubles, biens, getBien, getImmeuble, removeIntervention } =
    useAgenceData();
  const { proprietaires } = useProprietaires();
  const { can } = useEmployes();
  const [searchParams] = useSearchParams();

  const proprietaireParam = searchParams.get("proprietaire") ?? "";
  const immeubleParam = searchParams.get("immeuble") ?? "";
  const bienParam = searchParams.get("bien") ?? "";

  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [prioriteFilter, setPrioriteFilter] = useState("");
  const [proprietaireFilter, setProprietaireFilter] = useState(proprietaireParam);
  const [immeubleFilter, setImmeubleFilter] = useState(immeubleParam);
  const [bienFilter, setBienFilter] = useState(bienParam);
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<AgenceIntervention | null>(null);
  const [toDelete, setToDelete] = useState<AgenceIntervention | null>(null);

  const hasInterventions = interventions.length > 0;

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...immeubles
        .filter((immeuble) => !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    ],
    [immeubles, proprietaireFilter]
  );

  const bienOptions = useMemo(
    () => [
      { value: "", label: "Tous les biens" },
      ...biens
        .filter((bien) => !immeubleFilter || bien.immeubleId === immeubleFilter)
        .filter((bien) => !proprietaireFilter || bien.proprietaireId === proprietaireFilter)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    ],
    [biens, immeubleFilter, proprietaireFilter]
  );

  const statutOptions = useMemo(
    () => [{ value: "", label: "Tous les statuts" }, ...AGENCE_INTERVENTION_STATUT_OPTIONS],
    []
  );

  const prioriteOptions = useMemo(
    () => [{ value: "", label: "Toutes les priorités" }, ...AGENCE_INTERVENTION_PRIORITE_OPTIONS],
    []
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = interventions.filter((intervention) => {
      const matchesStatut = !statutFilter || intervention.statut === statutFilter;
      const matchesPriorite = !prioriteFilter || intervention.priorite === prioriteFilter;
      const matchesProprietaire = !proprietaireFilter || intervention.proprietaireId === proprietaireFilter;
      const matchesImmeuble = !immeubleFilter || intervention.immeubleId === immeubleFilter;
      const matchesBien = !bienFilter || intervention.bienId === bienFilter;

      const bien = intervention.bienId ? getBien(intervention.bienId) : undefined;
      const immeuble = intervention.immeubleId ? getImmeuble(intervention.immeubleId) : undefined;
      const matchesSearch =
        !term ||
        [
          intervention.probleme,
          intervention.description,
          intervention.prestataire,
          bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          immeuble?.nom ?? "",
          AGENCE_INTERVENTION_STATUT_LABELS[intervention.statut],
          AGENCE_INTERVENTION_PRIORITE_LABELS[intervention.priorite],
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return (
        matchesStatut &&
        matchesPriorite &&
        matchesProprietaire &&
        matchesImmeuble &&
        matchesBien &&
        matchesSearch
      );
    });

    return result.sort((a, b) => {
      if (sort === "cout") return b.cout - a.cout;
      if (sort === "ancien") return new Date(a.date).getTime() - new Date(b.date).getTime();
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [
    interventions,
    search,
    statutFilter,
    prioriteFilter,
    proprietaireFilter,
    immeubleFilter,
    bienFilter,
    sort,
    getBien,
    getImmeuble,
  ]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(statutFilter) ||
    Boolean(prioriteFilter) ||
    Boolean(proprietaireFilter) ||
    Boolean(immeubleFilter) ||
    Boolean(bienFilter);

  const resetFilters = () => {
    setSearch("");
    setStatutFilter("");
    setPrioriteFilter("");
    setProprietaireFilter("");
    setImmeubleFilter("");
    setBienFilter("");
  };

  const exportColumns: ExportColumn<AgenceIntervention>[] = [
    { header: "Problème", value: (intervention) => intervention.probleme },
    { header: "Priorité", value: (intervention) => AGENCE_INTERVENTION_PRIORITE_LABELS[intervention.priorite] },
    { header: "Statut", value: (intervention) => AGENCE_INTERVENTION_STATUT_LABELS[intervention.statut] },
    {
      header: "Propriétaire",
      value: (intervention) => {
        const owner = proprietaires.find((item) => item.id === intervention.proprietaireId);
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    { header: "Immeuble", value: (intervention) => getImmeuble(intervention.immeubleId)?.nom ?? "" },
    {
      header: "Bien",
      value: (intervention) => {
        const bien = intervention.bienId ? getBien(intervention.bienId) : undefined;
        return bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    { header: "Prestataire", value: (intervention) => intervention.prestataire },
    { header: "Coût (FCFA)", value: (intervention) => formatFcfa(intervention.cout) },
    { header: "Date", value: (intervention) => formatDate(intervention.date) },
  ];

  const columns: DataTableColumn<AgenceIntervention>[] = [
    {
      key: "probleme",
      header: "Problème",
      className: "min-w-[180px]",
      render: (intervention) => (
        <span className="font-label font-medium text-foreground-950">{intervention.probleme}</span>
      ),
    },
    {
      key: "emplacement",
      header: "Bien · Immeuble",
      className: "min-w-[190px]",
      render: (intervention) => {
        const bien = intervention.bienId ? getBien(intervention.bienId) : undefined;
        const immeuble = intervention.immeubleId ? getImmeuble(intervention.immeubleId) : undefined;
        const emplacement = [bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "", immeuble?.nom ?? ""]
          .filter(Boolean)
          .join(" · ");
        return <span className="text-foreground-700">{emplacement || "Aucun"}</span>;
      },
    },
    {
      key: "prestataire",
      header: "Prestataire",
      className: "min-w-[150px]",
      render: (intervention) =>
        intervention.prestataire ? (
          <span className="text-foreground-700">{intervention.prestataire}</span>
        ) : (
          <span className="text-foreground-400">—</span>
        ),
    },
    {
      key: "priorite",
      header: "Priorité",
      render: (intervention) => (
        <Badge tone={AGENCE_INTERVENTION_PRIORITE_TONES[intervention.priorite]}>
          {AGENCE_INTERVENTION_PRIORITE_LABELS[intervention.priorite]}
        </Badge>
      ),
    },
    {
      key: "statut",
      header: "Statut",
      render: (intervention) => (
        <Badge tone={AGENCE_INTERVENTION_STATUT_TONES[intervention.statut]}>
          {AGENCE_INTERVENTION_STATUT_LABELS[intervention.statut]}
        </Badge>
      ),
    },
    {
      key: "cout",
      header: "Coût",
      align: "right",
      className: "min-w-[110px]",
      render: (intervention) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(intervention.cout)}</span>
      ),
    },
    {
      key: "date",
      header: "Date",
      className: "min-w-[120px]",
      render: (intervention) => <span className="text-foreground-700">{formatDate(intervention.date)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[230px]",
      render: (intervention) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/agence/maintenance/${intervention.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions
            onEdit={can("maintenance.modifier") ? () => setEditing(intervention) : undefined}
            onDelete={can("maintenance.supprimer") ? () => setToDelete(intervention) : undefined}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Maintenance</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Suivez les interventions sur les immeubles et biens gérés par l'agence : problème, priorité, prestataire,
            coût et statut, du signalement jusqu'à la clôture.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-maintenance" title="Maintenance" />
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter une intervention
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-tools-line" label="Interventions" value={String(statsInterventions.total)} tone="primary" />
        <StatCard
          icon="ri-add-circle-line"
          label="Nouvelles"
          value={String(statsInterventions.nouveaux)}
          tone="accent"
        />
        <StatCard
          icon="ri-loader-4-line"
          label="En cours"
          value={String(statsInterventions.enCours)}
          tone="secondary"
        />
        <StatCard
          icon="ri-money-cny-circle-line"
          label="Coût total"
          value={formatFcfa(statsInterventions.coutTotal)}
          tone="neutral"
        />
      </div>

      {hasInterventions && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-int-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="agence-int-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Problème, prestataire, immeuble, bien..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-int-statut-filter" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="agence-int-statut-filter"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={statutOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-int-priorite-filter" className="text-xs font-label font-medium text-foreground-700">
                Priorité
              </label>
              <Select
                id="agence-int-priorite-filter"
                value={prioriteFilter}
                onChange={(event) => setPrioriteFilter(event.target.value)}
                options={prioriteOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="agence-int-proprietaire-filter"
                className="text-xs font-label font-medium text-foreground-700"
              >
                Propriétaire
              </label>
              <Select
                id="agence-int-proprietaire-filter"
                value={proprietaireFilter}
                onChange={(event) => {
                  setProprietaireFilter(event.target.value);
                  setImmeubleFilter("");
                  setBienFilter("");
                }}
                options={proprietaireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-int-immeuble-filter" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="agence-int-immeuble-filter"
                value={immeubleFilter}
                onChange={(event) => {
                  setImmeubleFilter(event.target.value);
                  setBienFilter("");
                }}
                options={immeubleOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-int-bien-filter" className="text-xs font-label font-medium text-foreground-700">
                Bien
              </label>
              <Select
                id="agence-int-bien-filter"
                value={bienFilter}
                onChange={(event) => setBienFilter(event.target.value)}
                options={bienOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} intervention{filtered.length > 1 ? "s" : ""} affichée
              {filtered.length > 1 ? "s" : ""} sur {interventions.length}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-44">
                <Select
                  aria-label="Trier par"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {!hasInterventions && (
        <EmptyState
          icon="ri-tools-line"
          title="Aucune intervention enregistrée"
          description="Ajoutez votre première intervention pour suivre les problèmes, leur priorité, le prestataire et le coût."
          action={
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter une intervention
            </button>
          }
        />
      )}

      {hasInterventions && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucune intervention ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(intervention) => intervention.id}
          minWidthClass="min-w-[1240px]"
          caption="Maintenance"
        />
      )}

      <InterventionForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        defaultProprietaireId={proprietaireParam || undefined}
        defaultImmeubleId={immeubleParam || undefined}
        defaultBienId={bienParam || undefined}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeIntervention(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer l'intervention"
        description={
          toDelete ? `Voulez-vous vraiment supprimer « ${toDelete.probleme} » ? Cette action est définitive.` : ""
        }
      />
    </div>
  );
}